// Laudo de vistoria e solicitação de correção (PDF no visual da Pílula Técnica): abrir, gerar nova versão e enviar
// pelo WhatsApp ao técnico responsável pelo atendimento e a quem fez a instalação, com o texto padrão já preenchido.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper,
  TextField, Typography,
} from "@material-ui/core";

import suporteApi, { mensagemErro } from "./api";
import { dataBR } from "./comum";
import { usePermissao } from "./permissoes";
import { SeletorTecnico } from "./SeletoresCadastro";

const NOME_DOC = { laudo: "Laudo de vistoria", correcao: "Solicitação de correção" };

export const abrirPdf = async (url) => {
  const janela = window.open("", "_blank"); // abre já no clique (bloqueador de pop-up)
  try {
    const { data } = await suporteApi.get(url, { responseType: "blob" });
    const local = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    if (janela) janela.location.href = local;
    else window.location.href = local;
  } catch (e) {
    if (janela) janela.close();
    toast.error(mensagemErro(e));
  }
};

const EnviarLaudo = ({ visitaId, open, onClose, onEnviado }) => {
  const [dados, setDados] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDados(null);
    suporteApi.get(`/api/supervisao/visitas/${visitaId}/envio`).then((r) => setDados(r.data))
      .catch((e) => { toast.error(mensagemErro(e)); onClose(); });
  }, [open, visitaId, onClose]);
  const mudar = (i, campo) => setDados((d) => ({ ...d, destinos: d.destinos.map((x, j) => (j === i ? { ...x, ...campo } : x)) }));
  const enviar = async () => {
    const destinos = dados.destinos.filter((x) => x.enviar)
      .map(({ papel, nome, telefone, texto, documentos }) => ({ papel, nome, telefone, texto, documentos }));
    if (!destinos.length) return toast.error("Marque ao menos um destinatário");
    setEnviando(true);
    try {
      const { data } = await suporteApi.post(`/api/supervisao/visitas/${visitaId}/envio`, { destinos });
      const quem = (r) => r.nome || (r.papel === "tecnico" ? "Técnico" : "Instalador");
      data.resultados.forEach((r) => (r.status === "ENVIADO"
        ? toast.success(`Enviado para ${quem(r)}`)
        : toast.error(`${quem(r)}: ${r.erro}`)));
      onEnviado();
      onClose();
    } catch (e) { toast.error(mensagemErro(e)); }
    setEnviando(false);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enviar laudo pelo WhatsApp</DialogTitle>
      <DialogContent>
        {!dados ? <CircularProgress size={24} /> : dados.destinos.map((d, i) => (
          <Paper key={d.papel} variant="outlined" style={{ padding: 12, marginBottom: 12, opacity: d.enviar ? 1 : 0.6 }}>
            <FormControlLabel control={<Checkbox color="primary" checked={!!d.enviar} onChange={(e) => mudar(i, { enviar: e.target.checked })} />}
              label={<b>{d.rotulo}</b>} />
            <SeletorTecnico label="Escolher do cadastro de técnicos (preenche nome e WhatsApp)" value={null}
              onChange={(t) => t && mudar(i, { nome: d.papel === "instalador" && t.empresa ? `${t.nome} (${t.empresa})` : t.nome,
                telefone: t.telefone1 || t.telefone2 || "", texto: d.texto.replace(/^Olá(, [^!]*)?!/, `Olá, ${t.nome}!`) })} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <TextField size="small" margin="dense" label="Nome" value={d.nome || ""} onChange={(e) => mudar(i, { nome: e.target.value })} style={{ flex: "1 1 220px" }} />
              <TextField size="small" margin="dense" label="WhatsApp (DDD + número)" value={d.telefone || ""}
                onChange={(e) => mudar(i, { telefone: e.target.value })} style={{ flex: "0 1 220px" }} />
            </div>
            <TextField fullWidth multiline minRows={6} variant="outlined" margin="dense" label="Mensagem (texto padrão, pode ajustar)"
              value={d.texto} onChange={(e) => mudar(i, { texto: e.target.value })} />
            <Typography variant="caption" color="textSecondary">Anexos: </Typography>
            {["laudo", "correcao"].filter((t) => dados.documentos[t]).map((t) => (
              <Chip key={t} size="small" style={{ margin: 2 }} label={`${NOME_DOC[t]} v${dados.documentos[t].versao}`}
                color={(d.documentos || []).includes(t) ? "primary" : "default"}
                onClick={() => mudar(i, { documentos: (d.documentos || []).includes(t) ? d.documentos.filter((x) => x !== t) : [...(d.documentos || []), t] })} />
            ))}
          </Paper>
        ))}
        <Typography variant="caption" color="textSecondary">
          A mensagem vai primeiro e os PDFs logo depois, pela conexão padrão do WhatsApp. O envio fica registrado na visita e no card.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={!dados || enviando} onClick={enviar}>{enviando ? "Enviando…" : "Enviar"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const LaudoDaVisita = ({ visita }) => {
  const [info, setInfo] = useState(null);
  const [envio, setEnvio] = useState(false);
  const podeValidar = usePermissao("supervisao.validar");
  const carregar = useCallback(() => {
    suporteApi.get(`/api/supervisao/visitas/${visita.id}/documentos`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [visita.id]);
  useEffect(() => { carregar(); }, [carregar, visita.status]);
  const fecharEnvio = useCallback(() => setEnvio(false), []);
  if (visita.status !== "CONCLUIDA") return null;
  const gerar = async () => {
    try {
      await suporteApi.post(`/api/supervisao/visitas/${visita.id}/laudo`);
      toast.success("Laudo gerado");
      carregar();
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  const docs = (info?.documentos || []);
  const ultimos = ["laudo", "correcao"].map((t) => docs.find((d) => d.tipo === t)).filter(Boolean);
  return (
    <Paper variant="outlined" style={{ padding: 12, marginTop: 12, borderColor: "#7dc033" }}>
      <Typography variant="subtitle2">Laudo de vistoria (PDF)</Typography>
      {!ultimos.length && <Typography variant="body2" color="textSecondary">Nenhum laudo gerado ainda.</Typography>}
      {ultimos.map((d) => (
        <Button key={d.id} size="small" variant="outlined" style={{ margin: "6px 6px 0 0" }}
          onClick={() => abrirPdf(`/api/supervisao/visitas/${visita.id}/documentos/${d.id}/pdf`)}>
          {`${NOME_DOC[d.tipo]} v${d.versao}`}
        </Button>
      ))}
      {podeValidar && (
        <div style={{ marginTop: 8 }}>
          <Button size="small" onClick={gerar}>{ultimos.length ? "Gerar nova versão" : "Gerar laudo"}</Button>
          <Button size="small" color="primary" variant="contained" disabled={!ultimos.length} onClick={() => setEnvio(true)}>Enviar laudo</Button>
        </div>
      )}
      {info?.formulario_correcao && (
        <Typography variant="caption" component="div" style={{ marginTop: 8 }}>
          {"Formulário de correção do técnico: "}
          <b>{{ ENVIADO: "aguardando o técnico", EM_APROVACAO: "respondido — aprovar na aba Correções", CORRECAO: "devolvido ao técnico",
            VALIDADO: "aprovado" }[info.formulario_correcao.status] || info.formulario_correcao.status}</b>
          {" · "}
          <a href={info.formulario_correcao.link} target="_blank" rel="noopener noreferrer">abrir</a>
          {" · "}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => { e.preventDefault(); navigator.clipboard?.writeText(info.formulario_correcao.link); toast.info("Link copiado"); }}>copiar link</a>
        </Typography>
      )}
      {(info?.envios || []).length > 0 && (
        <>
          <Typography variant="caption" component="div" style={{ marginTop: 8, fontWeight: 600 }}>Envios</Typography>
          {info.envios.map((e) => (
            <Typography key={e.id} variant="caption" component="div" style={{ color: e.status === "ERRO" ? "#c62828" : undefined }}>
              {`${dataBR(e.enviado_em)} · ${e.papel === "tecnico" ? "Técnico" : "Instalador"}: ${e.nome || "—"} (${e.telefone}) · `}
              {e.status === "ENVIADO" ? `enviado por ${e.por}` : `erro: ${e.erro}`}
            </Typography>
          ))}
        </>
      )}
      <EnviarLaudo visitaId={visita.id} open={envio} onClose={fecharEnvio} onEnviado={carregar} />
    </Paper>
  );
};

// Supervisão → Textos padrão: mensagens enviadas com o laudo (variáveis entre chaves)
export const TextosLaudo = () => {
  const [d, setD] = useState(null);
  const pode = usePermissao("supervisao.checklists");
  useEffect(() => { suporteApi.get("/api/supervisao/textos").then((r) => setD(r.data)).catch((e) => toast.error(mensagemErro(e))); }, []);
  if (!d) return <CircularProgress size={24} />;
  const salvar = async () => {
    try {
      setD((await suporteApi.put("/api/supervisao/textos", d.textos)).data);
      toast.success("Textos salvos");
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  const campo = (k, rotulo) => (
    <div style={{ marginBottom: 12 }}>
      <TextField fullWidth multiline minRows={8} variant="outlined" label={rotulo} value={d.textos[k]} disabled={!pode}
        onChange={(e) => setD({ ...d, textos: { ...d.textos, [k]: e.target.value } })} />
      {pode && <Button size="small" onClick={() => setD({ ...d, textos: { ...d.textos, [k]: d.padrao[k] } })}>Voltar ao texto padrão</Button>}
    </div>
  );
  return (
    <Paper variant="outlined" style={{ padding: 14, margin: "10px 0", maxWidth: 820 }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
        Mensagens que acompanham o laudo no WhatsApp. Antes de enviar dá para ajustar o texto de cada visita.
      </Typography>
      {campo("tecnico", "Para o técnico responsável pelo atendimento (vai com o laudo)")}
      {campo("instalador", "Para quem fez a instalação (vai com o laudo e a solicitação de correção)")}
      <Typography variant="caption" component="div" style={{ marginBottom: 8 }}>
        {"Variáveis: "}{Object.entries(d.variaveis).map(([k, v]) => `{${k}} = ${v}`).join(" · ")}
      </Typography>
      {pode && <Button color="primary" variant="contained" size="small" onClick={salvar}>Salvar</Button>}
    </Paper>
  );
};
